import { useState, useRef, useContext, useEffect } from "react";
import cn from "classnames";
import nodeComponents from "../../nodes";
import Draggable from "react-draggable";
import Inputs from "./Inputs";
import Outputs from "./Outputs";
import Controls from "./Controls";
import styles from "./style.module.css";
import ComputeContext from "../../store/ComputeContext";
import { scene } from "../../three/index";
import ContentEditable from "react-contenteditable";
import UIContext from "../../store/UIContext";
import nodeTypes from "../../nodes";

export default function Node({ node }) {
  const { methods: UIMethods } = useContext(UIContext);
  const { state: ComputeState, methods: ComputeMethods } =
    useContext(ComputeContext);

  const [disabled, setDisabled] = useState(true);

  const inputRef = useRef();

  useEffect(() => {
    const nodeComponent = nodeTypes.find((n) => n.name === node.component);

    ComputeMethods.computeNode(node.id, ComputeState.computedValues);

    const id = node.id;

    return () => {
      nodeComponent.outputs.forEach((output) => {
        const o = scene.getObjectByName(`${id}:${output.id}`);

        if (o) {
          scene.remove(o);
        }
      });
    };
  }, []);

  useEffect(() => {
    const id = node.id;

    if (scene) {
      nodeComponent.outputs.forEach((output) => {
        const o = scene.getObjectByName(`${id}:${output.id}`);

        if (o) {
          o.visible = node.visible;
        }
      });
    }
  }, [node.id, node.visible]);

  if (!node.component || !node.position) return null;

  const nodeComponent = nodeComponents.find((n) => n.name === node.component);

  function onKeyDown(e) {
    if (e.key === "Backspace" || e.key === "Delete") {
      UIMethods.removeNode(node.id);
    }
  }

  return (
    <Draggable
      styles={{ position: "absolute" }}
      handle=".handle"
      onDrag={(e, data) => {
        node.name = node.name;
        node.position.x = data.x;
        node.position.y = data.y;
      }}
      position={{
        x: node.position.x,
        y: node.position.y,
      }}
    >
      <div
        tabIndex="0"
        onKeyDown={onKeyDown}
        id={node.id}
        className={cn(
          styles.node,
          "handle",
          node.visible ? null : styles.nodeHidden
        )}
      >
        <header className={styles.nodeHeader}>
          <div></div>
          <div
            className={styles.nodeTitleWrapper}
            onDoubleClick={(e) => {
              setDisabled(false);
              setTimeout(() => {
                inputRef.current.focus();
              }, 10);
            }}
          >
            <ContentEditable
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              innerRef={inputRef}
              disabled={disabled}
              onBlur={() => setDisabled(true)}
              html={node.name}
              onChange={(e) => (node.name = e.target.value)}
              className={styles.nodeTitle}
            ></ContentEditable>
          </div>
          <button
            className={styles.hideButton}
            onClick={() => (node.visible = !node.visible)}
          >
            {node.visible ? (
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 422 302"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M404.32 106.96C392.039 90.5931 373.984 74.3981 351.609 60.4561L341.257 70.8081C344.601 72.8315 347.878 74.8823 351.018 77.0034C368.881 89.0504 383.241 102.507 392.948 115.48C402.71 128.445 407.596 140.906 407.534 150.441C407.597 159.973 402.702 172.433 392.948 185.406C378.389 204.894 353.362 225.359 321.745 240.715C290.128 256.106 251.972 266.473 210.995 266.469C190.815 266.477 171.315 263.786 152.925 259.149L141.355 270.719C163.125 276.946 186.539 280.672 210.996 280.68C268.844 280.66 320.996 261.254 358.956 235.657C377.948 222.841 393.409 208.473 404.311 193.919C415.166 179.36 421.674 164.591 421.733 150.439C421.675 136.291 415.167 121.513 404.311 106.962L404.32 106.96Z"
                  fill="currentColor"
                />
                <path
                  d="M366.39 12.2002C369.163 9.4268 369.163 4.9268 366.39 2.1572C363.617 -0.616202 359.12 -0.616202 356.347 2.1572L316.378 42.1262C285.44 28.8412 249.491 20.2082 211.008 20.2042C153.16 20.2237 101.008 39.6302 63.048 65.2272C44.056 78.0472 28.595 92.4152 17.693 106.965C6.83799 121.52 0.329996 136.289 0.270996 150.442C0.278808 157.044 2.00151 163.848 4.98981 170.786C10.2476 182.923 19.4588 195.583 31.8218 208.028C49.4668 225.743 73.6738 242.903 102.349 256.169L68.7118 289.806C65.9384 292.576 65.9384 297.076 68.7118 299.849C71.4852 302.623 75.9813 302.623 78.7548 299.849L366.39 12.2002ZM189.69 150.44C189.694 144.53 192.061 139.249 195.928 135.366C199.807 131.503 205.092 129.136 211.002 129.128C216.158 129.132 220.772 131.007 224.463 134.034L194.604 163.901C191.569 160.21 189.694 155.596 189.69 150.44L189.69 150.44ZM234.565 123.932C228.303 118.338 220.065 114.924 210.999 114.92C191.374 114.924 175.483 130.816 175.479 150.441C175.483 159.503 178.905 167.741 184.491 174.006L157.69 200.808C145.252 187.644 137.596 169.984 137.592 150.441C137.596 130.148 145.795 111.839 159.092 98.5345C172.401 85.2375 190.705 77.0385 210.998 77.0345C230.541 77.0384 248.193 84.6986 261.365 97.1325L234.565 123.932ZM71.045 222.147C53.158 209.799 38.756 196.17 29.018 183.362C24.143 176.964 20.4399 170.78 18.022 165.151C15.5845 159.53 14.4595 154.495 14.4673 150.44C14.4048 140.909 19.2993 128.448 29.0533 115.479C43.6123 95.9912 68.6393 75.5262 100.256 60.1672C131.873 44.7762 170.029 34.4092 211.006 34.4132C245.194 34.3976 277.385 41.6866 305.475 53.0302L271.393 87.1122C255.694 72.0962 234.455 62.8312 211.006 62.8272C162.615 62.8311 123.397 102.046 123.393 150.44C123.397 173.893 132.663 195.124 147.678 210.827L113.073 245.432C97.6513 238.686 83.4953 230.748 71.0503 222.147H71.045Z"
                  fill="currentColor"
                />
                <path
                  d="M211 223.84C204.066 223.84 197.395 222.813 191.047 221.024L179.832 232.235C189.527 235.934 200.008 238.051 211 238.051C259.391 238.047 298.609 198.828 298.613 150.438C298.613 139.446 296.5 128.965 292.797 119.27L281.586 130.481C283.375 136.833 284.402 143.504 284.402 150.438C284.398 170.727 276.199 189.036 262.902 202.344C249.597 215.633 231.293 223.836 211 223.84V223.84Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 422 262"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M404.32 87.53C387.968 65.725 361.355 44.229 327.965 27.96C294.578 11.718 254.383 0.772043 211.005 0.769043C153.157 0.788574 101.005 20.195 63.0451 45.792C44.0531 58.612 28.5921 72.98 17.6901 87.53C6.83806 102.089 0.331066 116.858 0.268066 131.003C0.275879 137.605 1.99856 144.409 4.98686 151.347C10.2447 163.484 19.4559 176.144 31.8189 188.589C50.3779 207.23 76.1309 225.296 106.784 238.808C137.432 252.316 172.991 261.234 211.014 261.238C268.862 261.219 321.014 241.812 358.974 216.215C377.966 203.399 393.427 189.031 404.329 174.477C415.184 159.918 421.692 145.149 421.751 130.997C421.692 116.86 415.184 102.091 404.329 87.5321L404.32 87.53ZM392.953 165.968C378.394 185.456 353.367 205.921 321.75 221.277C290.133 236.672 251.973 247.039 211 247.031C156.484 247.054 106.84 227.445 71.0401 202.715C53.1531 190.367 38.7511 176.738 29.0131 163.93C24.1381 157.532 20.435 151.348 18.0171 145.719C15.5796 140.094 14.4546 135.059 14.4624 131.004C14.3999 121.473 19.2944 109.012 29.0484 96.043C43.6074 76.555 68.6344 56.09 100.251 40.731C131.868 25.34 170.028 14.973 211.001 14.977C265.618 14.9575 315.291 33.438 351.021 57.567C368.884 69.614 383.244 83.0711 392.951 96.0441C402.713 109.009 407.599 121.47 407.537 131.005C407.6 140.54 402.709 153.001 392.955 165.97L392.953 165.968Z"
                  fill="currentColor"
                />
                <path
                  d="M211 43.3901C162.609 43.394 123.395 82.6051 123.391 130.999C123.395 179.39 162.614 218.608 211.004 218.612C259.395 218.608 298.613 179.389 298.617 130.999C298.609 82.6041 259.39 43.3941 211 43.3901ZM262.906 182.91C249.597 196.207 231.293 204.406 211 204.41C190.707 204.406 172.402 196.207 159.094 182.91C145.797 169.601 137.598 151.293 137.594 131.004C137.598 110.711 145.797 92.4021 159.094 79.0981C172.403 65.8011 190.707 57.6021 211 57.5981C231.293 57.602 249.598 65.8012 262.906 79.0981C276.203 92.4031 284.402 110.703 284.406 131.004C284.402 151.293 276.203 169.602 262.906 182.91Z"
                  fill="currentColor"
                />
                <path
                  d="M211 95.48C191.375 95.4839 175.484 111.375 175.48 131C175.484 150.625 191.375 166.516 211 166.52C230.625 166.516 246.516 150.625 246.52 131C246.516 111.371 230.625 95.484 211 95.48ZM226.074 146.07C222.191 149.937 216.91 152.304 211 152.308C205.09 152.304 199.809 149.937 195.926 146.07C192.063 142.187 189.695 136.906 189.692 130.996C189.695 125.086 192.063 119.805 195.93 115.922C199.809 112.059 205.094 109.691 211.004 109.684C216.914 109.688 222.195 112.055 226.078 115.922C229.941 119.801 232.308 125.086 232.316 130.996C232.308 136.906 229.941 142.187 226.074 146.07V146.07Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        </header>

        <div className={styles.nodeContent}>
          <Inputs node={node} nodeComponent={nodeComponent}></Inputs>
          <Controls node={node} nodeComponent={nodeComponent}></Controls>
          <Outputs node={node} nodeComponent={nodeComponent}></Outputs>
        </div>
      </div>
    </Draggable>
  );
}
